import type { BadgeTone } from '../../components/ui';
import type { LeaveRequest } from './queries';

/**
 * The words and shapes a leave request takes on screen.
 *
 * Pure, and separate from the page, so the naming can be tested without
 * rendering anything — and so drop 3's approver queue and drop 4's calendar
 * say exactly the same words as this screen does. Two screens inventing their
 * own name for CANCEL_REQUESTED is how a team ends up unsure whether a day is
 * off or not.
 */

/** What a person calls each state. Never the raw enum. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'Waiting for approval';
    case 'APPROVED':
      return 'Approved';
    case 'REJECTED':
      return 'Not approved';
    case 'CANCEL_REQUESTED':
      return 'Withdrawal requested';
    case 'CANCELLED':
      return 'Withdrawn';
    default:
      // A status added in the database and not yet here. Showing the raw value
      // is ugly and honest; inventing a friendly word for something unknown
      // would be neither.
      return status;
  }
}

export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'danger';
    case 'CANCEL_REQUESTED':
      return 'review';
    case 'CANCELLED':
      return 'neutral';
    default:
      return 'warning';
  }
}

/** "Full day", or the actual hours. A half day without its hours is a lie. */
export function durationLabel(
  request: Pick<LeaveRequest, 'duration_type' | 'start_time' | 'end_time'>,
): string {
  if (request.duration_type !== 'Half Day') {
    return 'Full day';
  }
  const from = shortTime(request.start_time);
  const to = shortTime(request.end_time);
  if (from === '' || to === '') {
    return 'Half day';
  }
  return `Half day, ${from} to ${to}`;
}

/** 09:00:00 -> 09:00. Anything unparseable comes back empty rather than wrong. */
export function shortTime(value: string | null): string {
  if (typeof value !== 'string') return '';
  const match = /^(\d{2}):(\d{2})/.exec(value);
  return match === null ? '' : `${match[1] ?? ''}:${match[2] ?? ''}`;
}

/**
 * Can this still be taken back, and what is the button called?
 *
 * PENDING withdraws outright — nobody has decided anything, so there is
 * nothing to ask permission for. APPROVED has to be asked for, because an
 * approver planned around the day being off. Everything else is finished.
 */
export function withdrawAction(status: string): { can: boolean; label: string } {
  if (status === 'PENDING') return { can: true, label: 'Withdraw' };
  if (status === 'APPROVED') return { can: true, label: 'Ask to withdraw' };
  return { can: false, label: '' };
}

/** Upcoming first, then the past. Both newest-first within themselves. */
export function splitByDate(
  requests: LeaveRequest[],
  today: string,
): { upcoming: LeaveRequest[]; past: LeaveRequest[] } {
  const upcoming: LeaveRequest[] = [];
  const past: LeaveRequest[] = [];
  for (const request of requests) {
    (request.leave_date >= today ? upcoming : past).push(request);
  }
  upcoming.sort((a, b) => a.leave_date.localeCompare(b.leave_date));
  past.sort((a, b) => b.leave_date.localeCompare(a.leave_date));
  return { upcoming, past };
}

/** Today, as the database writes dates. Local, because leave is a local day. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** Sat 4 October 2026. Long, because a leave date is worth being sure about. */
export function longDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Consecutive days, as one block.
 *
 * The database has already done the grouping — it has to, because the key
 * includes the reason and the reason does not leave it. This only walks the
 * rows and cuts them where group_no changes, which is why it takes anything
 * carrying a group_no rather than the full row type.
 */
export function blocksByGroup<T extends { group_no: number }>(rows: T[]): T[][] {
  const blocks: T[][] = [];
  for (const row of rows) {
    const last = blocks[blocks.length - 1];
    if (last !== undefined && last[0] !== undefined && last[0].group_no === row.group_no) {
      last.push(row);
    } else {
      blocks.push([row]);
    }
  }
  return blocks;
}

/**
 * "Sat 3 October 2026", or "Sat 3 October 2026 to Mon 5 October 2026".
 *
 * The Apps Script shows a range for a block and a single date for one day.
 */
export function blockDateLabel(dates: string[]): string {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first === undefined) return '';
  if (last === undefined || first === last) return longDate(first);
  return `${longDate(first)} to ${longDate(last)}`;
}

/**
 * "Nez-Full-Paid".
 *
 * The Apps Script builds this exact string for a calendar event: the person,
 * then Full or Half, then Paid for PTO and Unpaid for Unpaid Leave. Kept
 * character for character, hyphens included, because it is what the team
 * reads off the board today.
 */
export function calendarTitle(row: {
  employee_name: string;
  duration_type: string;
  leave_type: string;
}): string {
  const length = row.duration_type === 'Full Day' ? 'Full' : 'Half';
  const paid = row.leave_type === 'PTO' ? 'Paid' : 'Unpaid';
  return `${row.employee_name}-${length}-${paid}`;
}
